"use client";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useGamePageOldAnalytics } from "@/context/AnalyticsProvider/gamePageOld";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import Image from "next/image";

interface GamePageModalProps {
  title: string;
  gameTitle: string;
  gameIcon?: string;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  children: React.ReactNode;
  className?: string;
  gameId?: string;
}

export const GamePageModal = ({
  title,
  gameTitle,
  gameIcon,
  isModalOpen,
  setIsModalOpen,
  className,
  children,
  gameId,
}: GamePageModalProps) => {
  const { track } = useGamePageOldAnalytics();

  const handleClose = () => {
    if (gameId) {
      track({
        action: Action.PRESSED,
        extras: {
          id: "modal-close",
          control: "ModalClose",
          controlType: ControlType.BUTTON,
          gameId,
          gameName: gameTitle,
          version: "v3",
        },
      });
    }
    setIsModalOpen(false);
  };

  return (
    <Dialog open={isModalOpen}>
      <DialogPortal>
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{title}</DialogDescription>
        <DialogOverlay
          onClick={handleClose}
          className="fixed z-50 inset-0 bg-neutral-900/50 backdrop-blur-[32px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogContent
          className={cn(
            "fixed z-50 left-1/2 top-1/2 h-[90%] sm:w-[90%] w-full -translate-x-1/2 -translate-y-1/2 before:!opacity-100 after:hover:opacity-0 glass-container-border data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            className,
          )}
        >
          {/* Content container */}
          <div className="relative flex h-full flex-col rounded-[1.25rem] bg-neutral-900/50">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-neutral-50/15">
              <div className="flex items-center gap-5">
                {/* Game Icon */}
                {gameIcon ? (
                  <Image
                    src={gameIcon}
                    alt={gameTitle}
                    width={48}
                    height={48}
                    className="object-contain size-12 p-0.5 rounded-sm bg-neutral-900"
                  />
                ) : null}
                <p className="text-xs font-bold text-neutral-50">{title}</p>
              </div>

              {/* Close Button */}
              <DialogClose asChild>
                <button
                  type="button"
                  className="flex items-center justify-center w-[2.25rem] h-[2.25rem] rounded-[1.375rem] border-2 border-white/40 bg-white/5 hover:bg-white/10 transition-colors"
                  aria-label="Close"
                  onClick={handleClose}
                >
                  <X className="w-[1rem] h-[1rem] text-[#fafafa]" />
                </button>
              </DialogClose>
            </div>

            {children}
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
